import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type FormEvent } from "react";
import { downloadClientsApi } from "@/api/downloadClients";
import {
  pathMappingsApi,
  type PathMappingRow,
} from "@/api/pathMappings";
import { Button } from "@/components/ui/Button";
import { EmptyState } from "@/components/ui/EmptyState";
import { Field, NumberInput, Select, TextInput } from "@/components/ui/Field";
import { Modal } from "@/components/ui/Modal";
import { PageHeader } from "@/components/ui/PageHeader";
import { useToast } from "@/components/ui/Toast";

type EditingState =
  | { mode: "closed" }
  | { mode: "create" }
  | { mode: "edit"; row: PathMappingRow };

/**
 * Path Mappings settings page (#18). Users define
 * `(remote_prefix → local_prefix)` rewrites the importer applies to
 * paths reported by the download client. Without these, split
 * deployments — NZBGet on the host, arr-in-one in a container — see
 * "file not found" on every import because the client's path doesn't
 * resolve under arr-in-one's mount layout.
 *
 * Mappings can be scoped to one download client (when only one client
 * is split off the host fs) or left global (every client gets it).
 * The apply algorithm is longest-prefix-wins; see
 * `crates/core/src/path_mapping.rs`.
 */
export function PathMappings() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [editing, setEditing] = useState<EditingState>({ mode: "closed" });

  const { data: rows, isLoading } = useQuery({
    queryKey: ["path-mappings"],
    queryFn: pathMappingsApi.list,
  });

  // Loaded once for the form's "applies to" selector. The page can
  // render the table without it (we just print "client #N"), but the
  // form is much friendlier when it can show client names.
  const { data: clients } = useQuery({
    queryKey: ["download-clients"],
    queryFn: downloadClientsApi.list,
  });

  const removeMut = useMutation({
    mutationFn: (id: number) => pathMappingsApi.remove(id),
    onSuccess: () => {
      toast("Mapping removed", "success");
      qc.invalidateQueries({ queryKey: ["path-mappings"] });
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : "Remove failed", "error"),
  });

  const clientName = (id: number | null): string => {
    if (id === null) return "All clients";
    const c = clients?.find((c) => c.id === id);
    return c ? c.name : `Client #${id}`;
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Path mappings"
        description="Translate paths between the download client's view and arr-in-one's view. Use these when the importer reports 'file not found' on a completed download because the two run in different containers or one mounts the other over NFS."
        actions={
          <Button onClick={() => setEditing({ mode: "create" })}>
            Add mapping
          </Button>
        }
      />

      {isLoading ? (
        <p className="text-sm text-foreground/60">Loading…</p>
      ) : !rows || rows.length === 0 ? (
        <EmptyState
          title="No path mappings"
          description="If your download client and arr-in-one share the same filesystem layout, you don't need any. Add one when a download finishes but the importer can't find the file at the path the client reported."
          action={
            <Button onClick={() => setEditing({ mode: "create" })}>
              Add mapping
            </Button>
          }
        />
      ) : (
        <div className="overflow-x-auto rounded border border-line">
          <table className="w-full text-sm">
            <thead className="bg-surface-2/50 text-left text-xs uppercase text-foreground/60">
              <tr>
                <th className="px-4 py-2 font-medium">Remote prefix</th>
                <th className="px-4 py-2 font-medium">Local prefix</th>
                <th className="px-4 py-2 font-medium">Applies to</th>
                <th className="px-4 py-2 font-medium">Order</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="border-t border-line">
                  <td className="px-4 py-2 font-mono text-xs">
                    {row.remote_prefix}
                  </td>
                  <td className="px-4 py-2 font-mono text-xs">
                    {row.local_prefix}
                  </td>
                  <td className="px-4 py-2 text-foreground/75">
                    {clientName(row.applies_to)}
                  </td>
                  <td className="px-4 py-2 tabular-nums text-foreground/60">
                    {row.ord}
                  </td>
                  <td className="space-x-2 px-4 py-2 text-right">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setEditing({ mode: "edit", row })}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => {
                        if (
                          confirm(
                            `Remove mapping ${row.remote_prefix} → ${row.local_prefix}?`,
                          )
                        ) {
                          removeMut.mutate(row.id);
                        }
                      }}
                    >
                      Remove
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <PathMappingFormModal
        editing={editing}
        clients={clients ?? []}
        onClose={() => setEditing({ mode: "closed" })}
      />
    </div>
  );
}

interface FormState {
  remote_prefix: string;
  local_prefix: string;
  applies_to: string; // "" = global, otherwise a stringified id from the select
  ord: string;
}

const blankForm = (): FormState => ({
  remote_prefix: "",
  local_prefix: "",
  applies_to: "",
  ord: "0",
});
const formFromRow = (row: PathMappingRow): FormState => ({
  remote_prefix: row.remote_prefix,
  local_prefix: row.local_prefix,
  applies_to: row.applies_to === null ? "" : String(row.applies_to),
  ord: String(row.ord),
});

type ClientOption = { id: number; name: string };

function PathMappingFormModal({
  editing,
  clients,
  onClose,
}: {
  editing: EditingState;
  clients: ClientOption[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [form, setForm] = useState<FormState>(blankForm());
  const [submitting, setSubmitting] = useState(false);

  const open = editing.mode !== "closed";
  const isEdit = editing.mode === "edit";
  const editingRow = editing.mode === "edit" ? editing.row : null;

  useEffect(() => {
    if (editing.mode === "create") setForm(blankForm());
    if (editing.mode === "edit") setForm(formFromRow(editing.row));
  }, [editing]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // Number conversion: the select stores "" for "global" and a
      // stringified id otherwise. The PATCH endpoint distinguishes
      // missing (leave alone) from explicit null (clear), so on edit
      // we always send the field; on create we omit when "".
      const appliesAtNumber =
        form.applies_to === "" ? null : Number(form.applies_to);
      const ordNumber = Number(form.ord);
      if (isEdit && editingRow) {
        await pathMappingsApi.update(editingRow.id, {
          remote_prefix: form.remote_prefix.trim(),
          local_prefix: form.local_prefix.trim(),
          applies_to: appliesAtNumber,
          ord: Number.isFinite(ordNumber) ? ordNumber : 0,
        });
        toast("Mapping updated", "success");
      } else {
        await pathMappingsApi.create({
          remote_prefix: form.remote_prefix.trim(),
          local_prefix: form.local_prefix.trim(),
          applies_to: appliesAtNumber,
          ord: Number.isFinite(ordNumber) ? ordNumber : 0,
        });
        toast("Mapping created", "success");
      }
      qc.invalidateQueries({ queryKey: ["path-mappings"] });
      onClose();
    } catch (err) {
      toast(err instanceof Error ? err.message : "Save failed", "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? "Edit path mapping" : "Add path mapping"}
    >
      <form className="space-y-4" onSubmit={submit}>
        <Field
          label="Remote prefix"
          hint="The path as the download client reports it. Must start with /."
        >
          <TextInput
            autoFocus
            value={form.remote_prefix}
            onChange={(e) =>
              setForm({ ...form, remote_prefix: e.target.value })
            }
            placeholder="/data/downloads"
            required
          />
        </Field>
        <Field
          label="Local prefix"
          hint="What to substitute. Must start with /. Example: if /data/downloads on the client maps to /mnt/downloads inside the arr-in-one container, set this to /mnt/downloads."
        >
          <TextInput
            value={form.local_prefix}
            onChange={(e) =>
              setForm({ ...form, local_prefix: e.target.value })
            }
            placeholder="/mnt/downloads"
            required
          />
        </Field>
        <Field
          label="Applies to"
          hint="Leave blank for a global rule that applies to every download client."
        >
          <Select
            value={form.applies_to}
            onChange={(e) =>
              setForm({ ...form, applies_to: e.target.value })
            }
          >
            <option value="">All clients (global)</option>
            {clients.map((c) => (
              <option key={c.id} value={String(c.id)}>
                {c.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label="Order"
          hint="Tie-breaker when two rules match the same prefix length. Lower runs first; usually leave at 0."
        >
          <NumberInput
            value={form.ord}
            onChange={(e) => setForm({ ...form, ord: e.target.value })}
          />
        </Field>

        <div className="flex justify-end gap-2 pt-4">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={submitting}>
            {isEdit ? "Save" : "Create"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
